import React from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { forIn } from 'lodash'
import { flashErrorMessage } from 'redux-flash'
import * as apiActions from 'api-actions'
import {
  ServicesAgreementPlanType,
  ServicesAgreementPlanStatus,
  ActiveBenefitsPlanType,
  ALLOWABLE_BENEFIT_THRESHOLD,
} from 'config/automatic-rollovers'
import SharedPlanConfirmationView from './PlanConfirmationView'

const propTypes = {
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  setPlanConfirmationAcknowledgementsForm: PropTypes.func.isRequired,
  planConfirmationAcknowledgementsForm: PropTypes.object,
  fetchCompletedServicesAgreements: PropTypes.func.isRequired,
  updatePlanAgreement: PropTypes.func.isRequired,
  participantsAgreementHash: PropTypes.object,
  completedServicesAgreements: PropTypes.arrayOf(
    Types.servicesAgreementSummary
  ),
  setDiscardedParticipants: PropTypes.func.isRequired,
  discardedParticipants: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  participantFileName: PropTypes.string,
  currentUser: Types.user,
}

const defaultProps = {
  planConfirmationAcknowledgementsForm: {},
  participantsAgreementHash: null,
  completedServicesAgreements: null,
  participantFileName: null,
  currentUser: null,
}

export function sharedGetTerminatingDefinedBenefitParticipants(
  formValues,
  activeBenefitsType
) {
  let terminatingDefinedBenefitParticipants = []
  forIn(formValues, ({ planType, planStatus, participants }) => {
    if (
      planType === ServicesAgreementPlanType.DEFINED_BENEFIT &&
      planStatus === ServicesAgreementPlanStatus.TERMINATING
    ) {
      terminatingDefinedBenefitParticipants.push(...participants)
      activeBenefitsType.current = ActiveBenefitsPlanType.NONE
    }
  })
  return terminatingDefinedBenefitParticipants
}

export function createGetActiveBenefitAboveThresholdParticipants(
  getAmount,
  extraFilter = null
) {
  return function getActiveBenefitAboveThresholdParticipants(
    formValues,
    activeBenefitsType
  ) {
    let activeBenefitAboveThresholdParticipants = []
    forIn(formValues, ({ planType, planStatus, participants }) => {
      if (planStatus !== ServicesAgreementPlanStatus.ACTIVE) return
      if (planType === ServicesAgreementPlanType.DEFINED_BENEFIT)
        activeBenefitsType.current = ActiveBenefitsPlanType.DEFINED_BENEFIT
      if (planType === ServicesAgreementPlanType.DEFINED_CONTRIBUTION)
        activeBenefitsType.current = ActiveBenefitsPlanType.DEFINED_CONTRIBUTION
      const participantsAboveThreshold = participants.filter(
        (participant) =>
          getAmount(participant) > ALLOWABLE_BENEFIT_THRESHOLD &&
          (!extraFilter || extraFilter(participant))
      )
      activeBenefitAboveThresholdParticipants.push(
        ...participantsAboveThreshold
      )
    })
    return activeBenefitAboveThresholdParticipants
  }
}

export default function createPlanConfirmationView({
  PlanConfirmationFormComponent,
  agreementsMapping,
  nextPath,
  uploadFilePath,
  allParticipantsRemovedPath,
  recordType,
  getActiveBenefitAboveThresholdParticipants = null,
  getTerminatingDefinedBenefitParticipants = sharedGetTerminatingDefinedBenefitParticipants,
  selectors,
  actions,
  fetchAgreementsAction,
  agreementsSelectorFn,
}) {
  function PlanConfirmationView(props) {
    return (
      <SharedPlanConfirmationView
        {...props}
        agreementsMapping={agreementsMapping}
        nextPath={nextPath}
        uploadFilePath={uploadFilePath}
        allParticipantsRemovedPath={allParticipantsRemovedPath}
        recordType={recordType}
        PlanConfirmationFormComponent={PlanConfirmationFormComponent}
        getActiveBenefitAboveThresholdParticipants={
          getActiveBenefitAboveThresholdParticipants
        }
        getTerminatingDefinedBenefitParticipants={
          getTerminatingDefinedBenefitParticipants
        }
      />
    )
  }

  PlanConfirmationView.propTypes = propTypes
  PlanConfirmationView.defaultProps = defaultProps

  function mapStateToProps(state) {
    return {
      participantsForSubmission: selectors.participantsForSubmission(state),
      planConfirmationAcknowledgementsForm:
        selectors.planConfirmationAcknowledgementsForm(state),
      participantsAgreementHash: selectors.participantsAgreementHash(state),
      completedServicesAgreements: agreementsSelectorFn(state),
      discardedParticipants: selectors.discardedParticipants(state),
      participantFileName: selectors.participantFileName(state),
    }
  }

  const mapDispatchToProps = {
    flashErrorMessageHandler: flashErrorMessage,
    setPlanConfirmationAcknowledgementsForm:
      actions.setPlanConfirmationAcknowledgementsForm,
    fetchCompletedServicesAgreements: fetchAgreementsAction,
    updatePlanAgreement: apiActions.updatePlanAgreement,
    setDiscardedParticipants: actions.setDiscardedParticipants,
  }

  return compose(connect(mapStateToProps, mapDispatchToProps))(
    PlanConfirmationView
  )
}
